import './ReferenceDocs.scss';
import { ListGroup } from 'react-bootstrap';
import { MdPictureAsPdf } from 'react-icons/md';
import { Page } from '@src/Components/Page';

interface DocEntry {
  title: string;
  description: string;
  href: string;
}

interface DocSection {
  sectionTitle: string;
  docs: DocEntry[];
}

const DOC_SECTIONS: DocSection[] = [
  {
    sectionTitle: 'Data',
    docs: [
      {
        title: 'Reporting',
        description: "BPM's reporting capabilities and setup, along with samples.",
        href: 'https://cdn.blisspointmedia.com/reference/BPM Client Reporting.pdf'
      },
      {
        title: 'Onboarding',
        description: 'How BPM receives data from our clients and data requirements.',
        href: 'https://cdn.blisspointmedia.com/reference/BPM Data Onboarding Overview.pdf'
      },
      {
        title: 'Pixel Implementation',
        description: 'How to implement the BPM pixel for attribution.',
        href: 'https://cdn.blisspointmedia.com/reference/Tinuiti - Bliss Point Pixel Installation Guide.pdf'
      }
    ]
  },
  {
    sectionTitle: 'Creative',
    docs: [
      {
        title: 'Best Practices',
        description: 'Branded response best practices along with examples.',
        href: 'https://cdn.blisspointmedia.com/reference/Tinuiti - TVAD Creative Best Practices.pdf'
      },
      {
        title: 'S&P Guidelines',
        description: 'S&P Guidelines',
        href: 'https://cdn.blisspointmedia.com/reference/Tinuiti - Streaming S&P Guidelines and Regulations.pdf'
      },
      {
        title: 'Video Specs',
        description: 'Specs/requirements for streaming and linear creatives.',
        href: 'https://cdn.blisspointmedia.com/reference/ExtremeReachMasterFileSpecifications.pdf'
      },
      {
        title: 'Audio Specs',
        description: 'Specs/requirements for audio creatives.',
        href: 'https://cdn.blisspointmedia.com/reference/AudioSpecs.pdf'
      }
    ]
  },
  {
    sectionTitle: 'Media',
    docs: [
      {
        title: 'Podcast Advertising',
        description: 'Podcast partners/inventory, capabilities, and FAQ.',
        href: 'https://cdn.blisspointmedia.com/reference/Tinuiti - Podcast Advertising.pdf'
      },
      {
        title: 'Streaming Ecosystem',
        description: 'Overview of the streaming landscape and some of our media partners.',
        href: 'https://cdn.blisspointmedia.com/reference/Tinuiti - Streaming Ecosystem & Inventory.pdf'
      },
      {
        title: 'Ad Fraud Protection',
        description: 'The protective measures we put in place against ad fraud.',
        href: 'https://cdn.blisspointmedia.com/reference/Tinuiti - Streaming Ad Fraud Prevention.pdf'
      }
    ]
  },
  {
    sectionTitle: 'Attribution',
    docs: [
      {
        title: 'Streaming Attribution with Sample',
        description:
          'High level look at how we adjust for irresolvable identities in our streaming attribution.',
        href: 'https://cdn.blisspointmedia.com/reference/Tinuiti - Streaming Attribution.pdf'
      },
      {
        title: 'Linear Attribution',
        description: 'High level look at our linear attribution methodologies.',
        href: 'https://cdn.blisspointmedia.com/reference/Tinuiti - Linear TV Analytic Approach and Measurement.pdf'
      },
      {
        title: 'Fractional Attribution',
        description: 'An overview of our approach to multi-touch attribution.',
        href: 'https://cdn.blisspointmedia.com/reference/Tinuiti - Bliss Point - Fractional Attribution.pdf'
      }
    ]
  },
  {
    sectionTitle: 'Data Scientific Products',
    docs: [
      {
        title: 'The Two-Stage Advantage',
        description: "A technical guide to Tinuiti's Rapid Media Mix Model (rMMM).",
        href: 'https://cdn.blisspointmedia.com/reference/Tinuiti The Two-Stage Advantage.pdf'
      }
    ]
  },
  {
    sectionTitle: 'Glossary of Terms',
    docs: [
      {
        title: 'Glossary of Terms',
        description: 'Commonly used terms and acronyms in the video/audio space.',
        href: 'https://cdn.blisspointmedia.com/reference/BPM_PPM Media Update_ Glossary of Terms.pdf'
      }
    ]
  }
];

const ReferenceDocs = () => (
  <Page title='Reference Docs' pageType='Reference Docs'>
    <ListGroup className='reference-list'>
      {DOC_SECTIONS.map(section => (
        <DocSection key={section.sectionTitle} section={section} />
      ))}
    </ListGroup>
  </Page>
);

interface DocSectionProps {
  section: DocSection;
}

const DocSection = ({ section }: DocSectionProps) => (
  <>
    <ListGroup.Item className='section-header' data-testid='section-header'>
      {section.sectionTitle}
    </ListGroup.Item>
    {section.docs.map(doc => (
      <DocItem key={doc.title} doc={doc} />
    ))}
  </>
);

interface DocItemProps {
  doc: DocEntry;
}

const DocItem = ({ doc }: DocItemProps) => (
  <ListGroup.Item className='doc-item' data-testid='doc-item'>
    <a
      className='pdf-link'
      data-testid='pdf-link'
      target='_blank'
      rel='noopener noreferrer'
      href={doc.href}
    >
      <MdPictureAsPdf />
    </a>
    <div className='title'>{doc.title}</div>
    <div className='description'>{doc.description}</div>
  </ListGroup.Item>
);

export { ReferenceDocs };
